import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../src/layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_pluginrouting_6a683610 from 'nuxt_plugin_pluginrouting_6a683610' // Source: ./nuxt-i18n/plugin.routing.js (mode: 'all')
import nuxt_plugin_pluginmain_07aa2142 from 'nuxt_plugin_pluginmain_07aa2142' // Source: ./nuxt-i18n/plugin.main.js (mode: 'all')
import nuxt_plugin_apollomodule_17774e7c from 'nuxt_plugin_apollomodule_17774e7c' // Source: ./apollo-module.js (mode: 'all')
import nuxt_plugin_workbox_ad371adc from 'nuxt_plugin_workbox_ad371adc' // Source: ./workbox.js (mode: 'client')
import nuxt_plugin_metaplugin_6199f0b6 from 'nuxt_plugin_metaplugin_6199f0b6' // Source: ./pwa/meta.plugin.js (mode: 'all')
import nuxt_plugin_iconplugin_584aa5ce from 'nuxt_plugin_iconplugin_584aa5ce' // Source: ./pwa/icon.plugin.js (mode: 'all')
import nuxt_plugin_plugin_010c28b7 from 'nuxt_plugin_plugin_010c28b7' // Source: ./composition-api/plugin.js (mode: 'all')
import nuxt_plugin_localStorage_830ec59e from 'nuxt_plugin_localStorage_830ec59e' // Source: ../src/plugins/localStorage.js (mode: 'client')
import nuxt_plugin_sentry_e47869fc from 'nuxt_plugin_sentry_e47869fc' // Source: ../src/plugins/sentry.js (mode: 'all')
import nuxt_plugin_veevalidate_1a0c1998 from 'nuxt_plugin_veevalidate_1a0c1998' // Source: ../src/plugins/vee-validate.js (mode: 'all')
import nuxt_plugin_pubsub_5aad0384 from 'nuxt_plugin_pubsub_5aad0384' // Source: ../src/plugins/pubsub.js (mode: 'all')
import nuxt_plugin_config_3fddc0bf from 'nuxt_plugin_config_3fddc0bf' // Source: ../src/plugins/config.js (mode: 'all')
import nuxt_plugin_index_d32cfa5a from 'nuxt_plugin_index_d32cfa5a' // Source: ../src/plugins/notification/index.js (mode: 'all')
import nuxt_plugin_polyfills_7dce8c4e from 'nuxt_plugin_polyfills_7dce8c4e' // Source: ../src/plugins/polyfills (mode: 'client')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":true,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule
const baseStoreOptions = { preserveState: process.client }

function registerModule (path, rawModule, options = {}) {
  return originalRegisterModule.call(this, path, rawModule, { ...baseStoreOptions, ...options })
}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"meta":[{"charset":"utf-8"},{"name":"viewport","content":"initial-scale=1, minimum-scale=1, width=device-width, height=device-height"}],"link":[],"style":[],"script":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (typeof nuxt_plugin_pluginrouting_6a683610 === 'function') {
    await nuxt_plugin_pluginrouting_6a683610(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginmain_07aa2142 === 'function') {
    await nuxt_plugin_pluginmain_07aa2142(app.context, inject)
  }

  if (typeof nuxt_plugin_apollomodule_17774e7c === 'function') {
    await nuxt_plugin_apollomodule_17774e7c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_workbox_ad371adc === 'function') {
    await nuxt_plugin_workbox_ad371adc(app.context, inject)
  }

  if (typeof nuxt_plugin_metaplugin_6199f0b6 === 'function') {
    await nuxt_plugin_metaplugin_6199f0b6(app.context, inject)
  }

  if (typeof nuxt_plugin_iconplugin_584aa5ce === 'function') {
    await nuxt_plugin_iconplugin_584aa5ce(app.context, inject)
  }

  if (typeof nuxt_plugin_plugin_010c28b7 === 'function') {
    await nuxt_plugin_plugin_010c28b7(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_localStorage_830ec59e === 'function') {
    await nuxt_plugin_localStorage_830ec59e(app.context, inject)
  }

  if (typeof nuxt_plugin_sentry_e47869fc === 'function') {
    await nuxt_plugin_sentry_e47869fc(app.context, inject)
  }

  if (typeof nuxt_plugin_veevalidate_1a0c1998 === 'function') {
    await nuxt_plugin_veevalidate_1a0c1998(app.context, inject)
  }

  if (typeof nuxt_plugin_pubsub_5aad0384 === 'function') {
    await nuxt_plugin_pubsub_5aad0384(app.context, inject)
  }

  if (typeof nuxt_plugin_config_3fddc0bf === 'function') {
    await nuxt_plugin_config_3fddc0bf(app.context, inject)
  }

  if (typeof nuxt_plugin_index_d32cfa5a === 'function') {
    await nuxt_plugin_index_d32cfa5a(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_polyfills_7dce8c4e === 'function') {
    await nuxt_plugin_polyfills_7dce8c4e(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // If server-side, wait for async component to be resolved first
  if (process.server && ssrContext && ssrContext.url) {
    await new Promise((resolve, reject) => {
      router.push(ssrContext.url, resolve, (err) => {
        // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
        if (!err._isRouter) return reject(err)
        if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

        // navigated to a different route in router guard
        const unregister = router.afterEach(async (to, from) => {
          ssrContext.url = to.fullPath
          app.context.route = await getRouteData(to)
          app.context.params = to.params || {}
          app.context.query = to.query || {}
          unregister()
          resolve()
        })
      })
    })
  }

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
